import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Power, Volume2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

const noiseTypes = [
  { 
    value: "white", 
    label: "White Noise", 
    description: "Equal energy across all frequencies - bright, hissy static",
    color: "#FFFFFF"
  },
  { 
    value: "pink", 
    label: "Pink Noise", 
    description: "Warm, balanced sound like rain or ocean. Perfect for sleep.",
    color: "#FF69B4"
  },
  { 
    value: "brown", 
    label: "Brown Noise", 
    description: "Deep bass-heavy rumble like thunder. Excellent for focus.",
    color: "#8B4513"
  },
  { 
    value: "blue", 
    label: "Blue Noise", 
    description: "High-frequency emphasis - bright, airy texture.",
    color: "#4169E1"
  },
];

const BackgroundSounds = ({ audioContext, masterGain, isPlaying }) => {
  const [activeNoise, setActiveNoise] = useState(null);
  const [volume, setVolume] = useState(30);
  
  const noiseNodeRef = useRef(null);
  const gainNodeRef = useRef(null);
  const filterRef = useRef(null);

  const createNoiseBuffer = useCallback((type) => {
    if (!audioContext) return null;
    
    const bufferSize = audioContext.sampleRate * 2;
    const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
    const data = buffer.getChannelData(0);

    // Generate noise based on type
    if (type === "white") {
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
    } else if (type === "pink") {
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
        b6 = white * 0.115926;
      }
    } else if (type === "brown") {
      let lastOut = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        data[i] = (lastOut + (0.02 * white)) / 1.02;
        lastOut = data[i];
        data[i] *= 3.5;
      }
    } else if (type === "blue") {
      let lastOut = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        data[i] = white - lastOut;
        lastOut = white;
      }
    }

    return buffer;
  }, [audioContext]);

  const startNoise = useCallback((type) => {
    if (!audioContext || !masterGain) return;

    // Stop existing noise
    if (noiseNodeRef.current) {
      try {
        noiseNodeRef.current.stop();
        noiseNodeRef.current.disconnect();
      } catch (e) {}
    }

    const buffer = createNoiseBuffer(type);
    if (!buffer) return;

    const noiseNode = audioContext.createBufferSource();
    const gainNode = audioContext.createGain();
    
    noiseNode.buffer = buffer;
    noiseNode.loop = true;
    gainNode.gain.setValueAtTime(volume / 100, audioContext.currentTime);
    
    noiseNode.connect(gainNode);
    gainNode.connect(masterGain);
    noiseNode.start();
    
    noiseNodeRef.current = noiseNode;
    gainNodeRef.current = gainNode;
  }, [audioContext, masterGain, volume, createNoiseBuffer]);

  const stopNoise = useCallback(() => {
    if (noiseNodeRef.current) {
      try {
        noiseNodeRef.current.stop();
        noiseNodeRef.current.disconnect();
      } catch (e) {}
      noiseNodeRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (activeNoise && isPlaying) {
      startNoise(activeNoise);
    } else {
      stopNoise();
    }

    return () => stopNoise();
  }, [activeNoise, isPlaying, startNoise, stopNoise]);

  useEffect(() => {
    if (gainNodeRef.current && audioContext) {
      gainNodeRef.current.gain.setValueAtTime(volume / 100, audioContext.currentTime);
    }
  }, [volume, audioContext]);

  const toggleNoise = (type) => {
    if (activeNoise === type) {
      setActiveNoise(null);
    } else {
      setActiveNoise(type);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-300">Background Sounds</h4>
        <div className="flex items-center gap-2">
          <Volume2 size={14} className="text-gray-500" />
          <Slider
            value={[volume]}
            onValueChange={([v]) => setVolume(v)}
            min={0}
            max={100}
            step={1}
            data-testid="noise-volume"
            className="w-20"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {noiseTypes.map((noise) => (
          <motion.button
            key={noise.value}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => toggleNoise(noise.value)}
            data-testid={`noise-${noise.value}`}
            className={cn(
              "p-3 rounded-xl border text-left transition-all duration-200",
              activeNoise === noise.value
                ? "border-violet-500 bg-violet-500/20"
                : "border-white/10 bg-white/5 hover:border-white/20"
            )}
          >
            <div className="flex items-center gap-2 mb-1">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: noise.color, opacity: activeNoise === noise.value ? 1 : 0.5 }}
              />
              <span className="text-sm font-medium text-white">{noise.label}</span>
            </div>
            <p className="text-xs text-gray-500 line-clamp-2">{noise.description}</p>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default BackgroundSounds;
